/**
 * pt-BR formatters.
 *
 * Two notations coexist on purpose. Timing figures (lap times, deltas, gaps) use
 * the motorsport notation with a dot — `1:02.437`, `+0.128` — because that is what
 * every timing screen, transponder printout and MyChron display shows, and a comma
 * there reads as a typo to a driver. Physical quantities (speed, distance, g, file
 * sizes) use the pt-BR locale with a comma. The minus sign in a delta is U+2212,
 * not a hyphen, so it aligns with the plus sign in tabular figures.
 */

const MINUS = '−';
const NBSP = ' ';

const nf = (min: number, max: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: min, maximumFractionDigits: max });

const nf0 = nf(0, 0);
const nf1 = nf(1, 1);
const nf2 = nf(2, 2);

/** Rendered wherever a value is genuinely absent. Never render "0" for unknown. */
export const EM_DASH = '—';

function isNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

/**
 * `m:ss.mmm` — the canonical lap time. Hours are folded into minutes (a lap is
 * never an hour long, and a session duration reads fine as `72:04.120`).
 */
export function formatLapTime(ms: number | null | undefined): string {
  if (!isNum(ms)) return EM_DASH;
  const sign = ms < 0 ? MINUS : '';
  const abs = Math.abs(Math.round(ms));
  const minutes = Math.floor(abs / 60000);
  const seconds = Math.floor((abs % 60000) / 1000);
  const millis = abs % 1000;
  return `${sign}${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

/** `h:mm:ss` for long spans such as total driving time. */
export function formatDuration(seconds: number | null | undefined): string {
  if (!isNum(seconds)) return EM_DASH;
  const abs = Math.abs(Math.round(seconds));
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * A signed time difference in seconds: `+0.128`, `${MINUS}0.128`, `0.000`.
 * Always carries an explicit sign — colour is never the only signal.
 */
export function formatDelta(seconds: number | null | undefined, decimals = 3): string {
  if (!isNum(seconds)) return EM_DASH;
  const rounded = Number(seconds.toFixed(decimals));
  const abs = Math.abs(rounded).toFixed(decimals);
  if (rounded > 0) return `+${abs}`;
  if (rounded < 0) return `${MINUS}${abs}`;
  return `${(0).toFixed(decimals)}`;
}

/** Same as `formatDelta` but takes milliseconds. */
export function formatDeltaMs(ms: number | null | undefined, decimals = 3): string {
  return isNum(ms) ? formatDelta(ms / 1000, decimals) : EM_DASH;
}

/** Leaderboard gap. Rank 1 has a zero gap and reads as `—`. */
export function formatGap(gapMs: number | null | undefined, decimals = 3): string {
  if (!isNum(gapMs) || gapMs === 0) return EM_DASH;
  return `+${(gapMs / 1000).toFixed(decimals)}`;
}

/** The arrow that accompanies a delta so the sign is not carried by colour alone. */
export function deltaGlyph(value: number | null | undefined): '▲' | '▼' | '–' {
  if (!isNum(value) || value === 0) return '–';
  return value > 0 ? '▲' : '▼';
}

// ---------------------------------------------------------------------------
// Physical quantities
// ---------------------------------------------------------------------------

/** Telemetry stores speed in m/s; humans read km/h. */
export function toKmh(metersPerSecond: number): number {
  return metersPerSecond * 3.6;
}

export function formatSpeed(
  metersPerSecond: number | null | undefined,
  options: { unit?: boolean; decimals?: number } = {},
): string {
  if (!isNum(metersPerSecond)) return EM_DASH;
  const { unit = true, decimals = 1 } = options;
  const value = nf(decimals, decimals).format(toKmh(metersPerSecond));
  return unit ? `${value}${NBSP}km/h` : value;
}

/** Metres below 1 km, kilometres above it. */
export function formatDistance(
  meters: number | null | undefined,
  options: { unit?: boolean } = {},
): string {
  if (!isNum(meters)) return EM_DASH;
  const { unit = true } = options;
  if (Math.abs(meters) < 1000) {
    const value = nf0.format(Math.round(meters));
    return unit ? `${value}${NBSP}m` : value;
  }
  const value = nf2.format(meters / 1000);
  return unit ? `${value}${NBSP}km` : value;
}

export function formatKm(km: number | null | undefined, options: { unit?: boolean } = {}): string {
  if (!isNum(km)) return EM_DASH;
  const value = nf1.format(km);
  return options.unit === false ? value : `${value}${NBSP}km`;
}

export function formatG(g: number | null | undefined, options: { unit?: boolean } = {}): string {
  if (!isNum(g)) return EM_DASH;
  const value = nf2.format(g);
  return options.unit === false ? value : `${value}${NBSP}g`;
}

export function formatRpm(rpm: number | null | undefined, options: { unit?: boolean } = {}): string {
  if (!isNum(rpm)) return EM_DASH;
  const value = nf0.format(Math.round(rpm));
  return options.unit === false ? value : `${value}${NBSP}rpm`;
}

export function formatSeconds(
  seconds: number | null | undefined,
  options: { unit?: boolean; decimals?: number } = {},
): string {
  if (!isNum(seconds)) return EM_DASH;
  const { unit = true, decimals = 2 } = options;
  const value = nf(decimals, decimals).format(seconds);
  return unit ? `${value}${NBSP}s` : value;
}

export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (!isNum(value)) return EM_DASH;
  return nf(decimals, decimals).format(value);
}

export function formatPercent(fraction: number | null | undefined, decimals = 0): string {
  if (!isNum(fraction)) return EM_DASH;
  return `${nf(decimals, decimals).format(fraction * 100)}%`;
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!isNum(bytes)) return EM_DASH;
  if (bytes < 1024) return `${nf0.format(bytes)}${NBSP}B`;
  const units = ['kB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${(value >= 100 ? nf0 : nf1).format(value)}${NBSP}${units[unit]}`;
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const dateFmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const dateLongFmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const dateTimeFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const dateTimeSecondsFmt = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  return date ? dateFmt.format(date) : EM_DASH;
}

export function formatDateLong(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  return date ? dateLongFmt.format(date) : EM_DASH;
}

export function formatTimeOfDay(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  return date ? timeFmt.format(date) : EM_DASH;
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  options: { seconds?: boolean } = {},
): string {
  const date = toDate(value);
  if (!date) return EM_DASH;
  return (options.seconds ? dateTimeSecondsFmt : dateTimeFmt).format(date);
}

const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

/** `agora mesmo`, `há 4 min`, `ontem`, `há 3 meses`. */
export function formatRelativeTime(
  value: string | number | Date | null | undefined,
  now: Date = new Date(),
): string {
  const date = toDate(value);
  if (!date) return EM_DASH;
  const diffS = (date.getTime() - now.getTime()) / 1000;
  const abs = Math.abs(diffS);
  if (abs < 45) return 'agora mesmo';
  if (abs < 3600) return rtf.format(Math.round(diffS / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffS / 3600), 'hour');
  if (abs < 2592000) return rtf.format(Math.round(diffS / 86400), 'day');
  if (abs < 31536000) return rtf.format(Math.round(diffS / 2592000), 'month');
  return rtf.format(Math.round(diffS / 31536000), 'year');
}

// ---------------------------------------------------------------------------
// Domain labels
// ---------------------------------------------------------------------------

export const VISIBILITY_LABEL: Record<string, string> = {
  private: 'Privada',
  network: 'Rede',
  public: 'Pública',
};

export const USER_STATUS_LABEL: Record<string, string> = {
  active: 'Ativo',
  suspended: 'Suspenso',
  pending: 'Pendente',
};

export const USER_ROLE_LABEL: Record<string, string> = {
  user: 'Piloto',
  admin: 'Administrador',
};

export const INVALID_REASON_LABEL: Record<string, string> = {
  'short-distance': 'Distância curta',
  'long-distance': 'Distância longa',
  'vehicle-stopped': 'Kart parado',
  'duration-out-of-range': 'Duração fora da faixa',
  'incomplete-gps': 'GPS incompleto',
  'out-lap': 'Volta de saída',
  'in-lap': 'Volta de entrada',
};

export const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Crítico',
  major: 'Importante',
  minor: 'Menor',
  strength: 'Ponto forte',
};

/** Two-letter monogram for avatars. Falls back to the e-mail when there is no name. */
export function initials(name: string | null | undefined): string {
  const source = (name ?? '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}
